import React, { PropsWithChildren, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Clip } from '@ff-os/rendering-shared/Clip';
import { shiftCropToFaceCentered } from '@ff-os/shared-utils/crop/shiftCropToFaceCentered';
import { sliceCropToAspect } from '@ff-os/shared-utils/crop/sliceCropToSize';
import { Area } from '@ff-os/shared-utils/types/Area';
import { Size } from '@ff-os/shared-utils/types/Size';
import type { FaceDetection } from 'ff-face-detection';
import { Button } from '../../buttons/button/Button';
import { WindowFooter } from '../../window/partials/WindowFooter';
import { WindowHeader } from '../../window/partials/WindowHeader';
import { Window } from '../../window/Window';
import { PhotoEditWindowBody } from './PhotoEditWindowBody';
import styles from './PhotoEditWindowBody.module.scss';

export const PhotoEditWindow = ({
    onConfirm, close, save, overlayUrl, fileName,
    activeCropBox, imageSize, aspectRatio,
    clip, filter, updateFilter,
    enableAiStudio, telemetryCategory, aiStudioStartupAppId,
    autoFaceCenter, detections,
    pluginId,
    uploadFiles, children
}: Props) => {

    const { t } = useTranslation();

    const [cropBox, setCropBox] = useState<Area>(activeCropBox);

    const closeAndConfirm = useCallback(() => {
        save(cropBox);
        close();
        onConfirm && onConfirm();
    }, [close, onConfirm, cropBox, save]);

    useEffect(() => {

        let cropBox = aspectRatio
            ? sliceCropToAspect(activeCropBox, aspectRatio)
            : activeCropBox;

        if (autoFaceCenter && detections) {
            cropBox = shiftCropToFaceCentered(cropBox, detections, imageSize);
        }

        setCropBox(cropBox);
    }, [activeCropBox, aspectRatio, autoFaceCenter, detections, imageSize]);

    return (
        <Window close={close} fullHeight>
            <WindowHeader previous={close} className={styles.header}>
                {t('image_uploader.edit')}
            </WindowHeader>

            <PhotoEditWindowBody
                overlayUrl={overlayUrl}
                fileName={fileName}
                activeCropBox={cropBox}
                setCropBox={setCropBox}
                imageSize={imageSize}
                clip={clip}
                aspectRatio={aspectRatio}
                filter={filter}
                updateFilter={updateFilter}
                enableAiStudio={enableAiStudio}
                telemetryCategory={telemetryCategory}
                aiStudioStartupAppId={aiStudioStartupAppId}
                uploadFiles={uploadFiles}
                pluginId={pluginId}>
                {children}
            </PhotoEditWindowBody>

            <WindowFooter>
                <Button style='outline' onClick={close}>
                    {t('commons.cancel')}
                </Button>

                <Button onClick={closeAndConfirm}>
                    {t('commons.save')}
                </Button>
            </WindowFooter>
        </Window>
    );
};

type Props = PropsWithChildren<{

    overlayUrl: string;
    fileName?: string;

    onConfirm?: () => void;
    close: () => void;
    save: (crop: Area) => void;

    activeCropBox: Area;
    imageSize: Size;
    clip?: Clip;
    aspectRatio?: number;

    filter: string | null;
    updateFilter: (filter: string | null) => void;
    uploadFiles?: (files: File) => void;

    enableAiStudio?: boolean;
    telemetryCategory?: string;
    aiStudioStartupAppId?: string;
    pluginId?: string;

    autoFaceCenter?: boolean;
    detections?: FaceDetection[];
}>;